import React, { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import LOCALSTORAGE from '../../_shared/constants/localStorage';
import { BUSINESS_UNIT } from '../../_shared/constants/omp';
import ReducerConstants from '../../_shared/constants/reducers';

const OmpDealerContext = React.createContext({});

export const useOmpDealerContext = () => useContext(OmpDealerContext);

const OmpDealerProvider = ({
  children,
  isOMP,
  validOmpDealerships,
  vdpNetwork,
  biddingDealerId,
  tradeDetailsId,
  isOmpMultiOrgDealer
}) => {
  const ompUserNetworks = useSelector(
    state => state[ReducerConstants.AUTH]?.nwContext?.tradeRevContext?.context,
    shallowEqual
  );
  const [ompBiddingDealer, setOmpBiddingDealer] = useState(undefined);

  const isMultiOrgAndEnabled = useMemo(
    () => isOMP && isOmpMultiOrgDealer && validOmpDealerships?.length > 1,
    [isOMP, isOmpMultiOrgDealer, validOmpDealerships]
  );

  const ompDefaultDealer = useMemo(() => {
    const storedEcsContext = localStorage.getItem(
      LOCALSTORAGE.ECS_PROFILE_CONTEXT
    );
    const organizations =
      storedEcsContext && JSON.parse(storedEcsContext)?.organizationSummaries;

    return organizations?.find(org => org.isDefault);
  }, []);

  const filteredOmpDealerships = useMemo(() => {
    if (!ompUserNetworks || !vdpNetwork) return validOmpDealerships;

    const networkValidDealerIds = ompUserNetworks
      .filter(dealership =>
        dealership.networks?.buyingNetworks?.find(
          network => network.id === parseInt(vdpNetwork?.id, 10)
        )
      )
      .map(dealership => dealership.dealerId);

    const networkValidDealerships = validOmpDealerships?.filter(
      dealership =>
        dealership.sourceInfo?.find(
          source =>
            source?.businessUnit === BUSINESS_UNIT.TRADEREV &&
            networkValidDealerIds.includes(parseInt(source.sourceId, 10))
        ) &&
        dealership.paymentAccountSummaries &&
        Object.keys(dealership.paymentAccountSummaries).length > 0
    );

    return networkValidDealerships;
  }, [ompUserNetworks, vdpNetwork, validOmpDealerships]);

  useEffect(() => {
    /**
     * Only run effect when the there are filteredOmpDealerships
     * and trade/details call has returned
     */
    if ((!filteredOmpDealerships && !validOmpDealerships) || !tradeDetailsId) {
      return;
    }

    /**
     * if biddingDealerId is present set omp bidding dealer to associated dealer
     * else set bidding dealer to currently selected multi org dealer
     */
    if (biddingDealerId) {
      setOmpBiddingDealer(
        filteredOmpDealerships?.find(dealership =>
          dealership.sourceInfo.find(
            source =>
              source.businessUnit === BUSINESS_UNIT.TRADEREV &&
              parseInt(source.sourceId, 10) === parseInt(biddingDealerId, 10)
          )
        )
      );
    } else {
      setOmpBiddingDealer(
        validOmpDealerships?.find(dealership => dealership.isCurrent)
      );
    }
  }, [
    filteredOmpDealerships,
    biddingDealerId,
    tradeDetailsId,
    validOmpDealerships
  ]);

  const ContextValue = useMemo(
    () => ({
      ompBiddingDealer,
      ompDefaultDealer,
      setOmpBiddingDealer,
      validOmpDealerships: filteredOmpDealerships,
      isMultiOrgAndEnabled,
      isOmpMultiOrgDealer
    }),
    [
      ompBiddingDealer,
      ompDefaultDealer,
      filteredOmpDealerships,
      isMultiOrgAndEnabled,
      isOmpMultiOrgDealer
    ]
  );

  return (
    <OmpDealerContext.Provider value={ContextValue}>
      {children}
    </OmpDealerContext.Provider>
  );
};

export default OmpDealerProvider;
